// Slug ↔ bare-string mapping for the patient domain (batch 007 / ADR-A3).
//
// The backend stores and transmits enum values as SLUGS (`#PatientStatus.Active`,
// `#Sex.Female`), following this repo's established enum-like convention — the
// role type does the same. The frontend, meanwhile, keys EVERYTHING about status
// on the legacy bare strings, and every one of those consumers is typed `any`:
//
//   · getPatientStatusLabel indexes TransferPatientStatus by the bare value and
//     falls back to `cdb_patient_status_<value>_text`;
//   · TransferPatientStatusColor / PatientStatusColor index by the bare value to
//     colour the status pill;
//   · the edit dropdown compares against TransferPatientStatus.pending and
//     .ehospitalet to HIDE the two transfer-machine-only states on naestved.
//
// A slug reaching any of them fails silently — TypeScript cannot see it. The
// dropdown would render a raw i18n key, the pills would lose their colour, and
// the hide rule would stop matching, letting a nurse hand-set a patient into a
// status only the transfer state machine is supposed to produce. That last one
// is a clinical consequence, which is why the mapping is centralised HERE, in
// one place crossed by every response, rather than pushed into render code.
//
// ⚠️ EXPLICIT TABLES, NOT A DERIVATION. Stripping the prefix and un-camel-casing
// would look equivalent and would be right for nine of the eleven values —
// `Ehospitalet` would derive to `ehospitalet` only by luck of capitalisation, and
// a backend spelling of `EHospitalet` would silently yield `e_hospitalet`. This
// repo has been bitten by exactly that shape before: a language table whose
// English value happened to be correct while its Danish value was a display name
// rather than the wire value, so the bug only appeared in one locale. A
// half-correct mapping survives smoke testing; a missing table entry does not.

// The full enum, 11 values. `admitted` and `home_admission` are legacy values
// still present in the frontend's i18n bundles; `discharged_self_managed` exists
// in the legacy language table and both locale files, and is included because
// nothing proves the other tenants do not use it.
const STATUS_SLUG_TO_BARE: Record<string, string> = {
  '#PatientStatus.Active': 'active',
  '#PatientStatus.Inactive': 'inactive',
  '#PatientStatus.Admitted': 'admitted',
  '#PatientStatus.HomeAdmission': 'home_admission',
  '#PatientStatus.Ehospitalet': 'ehospitalet',
  '#PatientStatus.Hospitalet': 'hospitalet',
  '#PatientStatus.Deceased': 'deceased',
  '#PatientStatus.Pending': 'pending',
  '#PatientStatus.Rejected': 'rejected',
  '#PatientStatus.Discharged': 'discharged',
  '#PatientStatus.DischargedSelfManaged': 'discharged_self_managed',
};

// The frozen form submits these as bare lowercase `<option value>`s.
const SEX_SLUG_TO_BARE: Record<string, string> = {
  '#Sex.Unknown': 'unknown',
  '#Sex.Female': 'female',
  '#Sex.Male': 'male',
};

// Batch 010 / ADR-7 — the patient list's alarm band. `alarm_state.severity` stores
// and the wire serves the `#Severity.*` SLUG (root's 2026-08-05 ruling, after this
// executor reported that the frozen text supported both readings), so the same
// boundary that un-slugs status and sex un-slugs this too.
//
// ⚠️ Its failure mode is the nastiest on the list: the bell is rendered by
// comparing this value against the client's own bare severity words, and an
// unmatched value falls into the indicator's default branch — so the WHOLE alarm
// column silently stops rendering while every other cell in the row looks fine.
// The five values are 009's severity vocabulary; GRAY and GREENCHECKED are
// display-only states the backend never sends (they are driven by
// `all_measurement_is_ack` plus an absent band, in the renderer).
//
// SLUG ONLY, deliberately not "either spelling": the question of which form the
// wire carries has been ruled, and a table that accepts both could never tell us
// the backend had drifted.
const SEVERITY_SLUG_TO_BARE: Record<string, string> = {
  '#Severity.Unknown': 'unknown',
  '#Severity.Green': 'green',
  '#Severity.Yellow': 'yellow',
  '#Severity.Orange': 'orange',
  '#Severity.Red': 'red',
};

const BARE_TO_STATUS_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(STATUS_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);

const BARE_TO_SEX_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(SEX_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);

// An unmapped value is a contract breach: the enum is closed and the write-path
// hook rejects anything outside it, so this can only mean the table drifted from
// the enum. It is reported rather than swallowed, and the original value is
// returned unchanged so one unknown status cannot blank an entire patient list.
const translate = (
  table: Record<string, string>,
  value: string | undefined,
  kind: string
): string | undefined => {
  if (!value) {
    return value;
  }
  const mapped = table[value];
  if (mapped) {
    return mapped;
  }
  if (value.startsWith('#')) {
    // eslint-disable-next-line no-console
    console.error(
      `[patient-slug] no ${kind} mapping for "${value}" — the table has drifted from the contract enum`
    );
  }
  return value;
};

export const statusSlugToBare = (slug: string | undefined) =>
  translate(STATUS_SLUG_TO_BARE, slug, 'status');

export const sexSlugToBare = (slug: string | undefined) =>
  translate(SEX_SLUG_TO_BARE, slug, 'sex');

// Outbound: the form edits bare strings, the contract wants slugs.
export const statusBareToSlug = (bare: string | undefined) =>
  translate(BARE_TO_STATUS_SLUG, bare, 'status');

export const sexBareToSlug = (bare: string | undefined) =>
  translate(BARE_TO_SEX_SLUG, bare, 'sex');

export const severitySlugToBare = (slug: string | undefined) =>
  translate(SEVERITY_SLUG_TO_BARE, slug, 'severity');

// Outbound: the alarm filter's options are the bare words, the contract's
// `repeated string last_alarm` filter takes slugs. Same direction as status.
const BARE_TO_SEVERITY_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(SEVERITY_SLUG_TO_BARE).map(([slug, bare]) => [bare, slug])
);

export const severityBareToSlug = (bare: string | undefined) =>
  translate(BARE_TO_SEVERITY_SLUG, bare, 'severity');

// ─────────────────────────────────────────────────────────────────────────────
// camelCase (wire) → snake_case (consumers)
//
// ⚠️ FOUND BY THE ASSEMBLED-SYSTEM GATE, not by any test. The backend serialises
// responses with protojson, whose DEFAULT renames snake_case proto fields to
// lowerCamel: `first_name` goes out as `firstName`, `total_records` as
// `totalRecords`, `is_default_filter` as `isDefaultFilter`. Requests are the other
// direction — bodies carry the snake proto names. api.types.ts states that rule in
// three places and this file's first version ignored all three.
//
// The failure was invisible in every unit test and silent at runtime: multi-word
// fields resolved to `undefined` while SINGLE-word ones (`id`, `cpr`, `sex`,
// `status`, `pinned`) were accidentally correct — so the patient list rendered every
// name and birth date blank, the pager read "0 of 0" over a full table, and the page
// still looked populated rather than broken. Exactly the half-correct-mapping shape
// that survives a smoke test.
//
// Applied by every patient transformResponse so consumers keep the snake_case
// names the rest of this front end uses. Deep, because it has to reach nested
// `relative[]` entries — those carry `firstName` too, and the next-of-kin table
// renders `first_name`.
const camelToSnake = (key: string) =>
  key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const snakeKeysFromWire = (value: any): any => {
  if (Array.isArray(value)) {
    return value.map(snakeKeysFromWire);
  }
  // Plain objects only: Date and friends must pass through untouched.
  if (
    value === null ||
    typeof value !== 'object' ||
    value.constructor !== Object
  ) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => [
      camelToSnake(key),
      snakeKeysFromWire(inner),
    ])
  );
};

// Applied by every transformResponse that carries a patient row, so no render
// path ever sees a slug.
export const patientRowFromWire = (row: any): any => {
  const snake = snakeKeysFromWire(row);
  return {
    ...snake,
    status: statusSlugToBare(snake.status),
    sex: sexSlugToBare(snake.sex),
    // Batch 010 / ADR-7. Absent on rows for patients who have never submitted a
    // measurement, and an empty string when nothing is unacknowledged — both pass
    // through untouched, which is what the indicator's grey/tick branches read.
    //
    // ⚠️ STILL UN-SLUGGED EVEN THOUGH THE BELL NO LONGER RENDERS FROM IT. It is
    // the SEVERITY FILTER's round trip (`?last_alarm=red` in the address bar ->
    // `#Severity.Red` on the wire) and the sortable column key, both of which
    // still speak the bare words.
    last_alarm: severitySlugToBare(snake.last_alarm),
    // Batch 021 / ADR-7 — the field the bell actually renders from, paired with
    // `all_measurement_is_ack`.
    //
    // WHY BOTH ARE UN-SLUGGED HERE, in the same place, rather than one of them at
    // the point of use: an unmapped value falls through AlarmIndicator's switch to
    // its default branch, so a slug reaching it does not throw — it silently blanks
    // the whole alarm column while every other cell in the row looks fine. That is
    // the failure this file exists to make impossible, and it does not become less
    // likely for the second field.
    //
    // Empty string means "never measured" and passes through untouched, exactly as
    // `last_alarm`'s empty means "nothing unacknowledged". The two emptinesses are
    // NOT the same fact and must not be conflated (alarm_state.go:72-79).
    latest_alarm: severitySlugToBare(snake.latest_alarm),
  };
};
